import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Body } from '~/components/atom/Typography';

export type Props = {
  length: number;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onComplete?: () => void;
  errorMessage?: string;
  disabled?: boolean;
  placeholder?: string;
  dataTestId?: string;
  className?: string;
  autoFocus?: boolean;
};

const onlyDigits = (s: string) => s.replace(/\D/g, '');

const OTPInput: React.FCC<Props> = ({
  length,
  value = '',
  onChange,
  onBlur,
  onComplete,
  errorMessage,
  disabled = false,
  placeholder,
  dataTestId,
  className,
  autoFocus = false,
}) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const normalizedExternalValue = useMemo(
    () => onlyDigits(value).slice(0, length),
    [value, length],
  );

  const [otpValues, setOtpValues] = useState<Array<string>>(
    Array.from({ length }, (_, index) => normalizedExternalValue[index] || ''),
  );

  // Keep local state in sync with external value
  useEffect(() => {
    const next = Array.from(
      { length },
      (_, index) => normalizedExternalValue[index] || '',
    );
    setOtpValues(next);
  }, [normalizedExternalValue, length]);

  // Handle autoFocus
  useEffect(() => {
    if (autoFocus && !disabled) {
      const raf = requestAnimationFrame(() => {
        inputRefs.current[0]?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
    return;
  }, [autoFocus, disabled]);

  const focusIndex = useCallback((idx: number) => {
    inputRefs.current[idx]?.focus();
  }, []);

  const applyOtpValues = useCallback(
    (nextOtpValues: Array<string>) => {
      setOtpValues(nextOtpValues);
      const combined = nextOtpValues.join('');
      onChange?.(combined);

      if (combined.length === length && onComplete) {
        onComplete();
      }
    },
    [length, onChange, onComplete],
  );

  const applyAtIndex = useCallback(
    (startIndex: number, raw: string) => {
      const digits = onlyDigits(raw);
      if (!digits) return;

      const next = [...otpValues];
      let i = startIndex;

      for (const char of digits) {
        if (i >= length) break;
        next[i] = char;
        i += 1;
      }

      applyOtpValues(next);

      // Move focus to the next empty slot (or last)
      const nextFocus = Math.min(i, length - 1);
      focusIndex(nextFocus);
    },
    [otpValues, length, applyOtpValues, focusIndex],
  );

  const handleOtpChange = useCallback(
    (index: number, inputValue: string) => {
      if (disabled) return;

      // Covers:
      // - normal typing (single digit)
      // - paste / iOS one-time-code / Android autofill (multiple digits)
      const digits = onlyDigits(inputValue);

      if (!digits) {
        const next = [...otpValues];
        next[index] = '';
        applyOtpValues(next);
        return;
      }

      if (digits.length === 1) {
        const next = [...otpValues];
        next[index] = digits;
        applyOtpValues(next);

        // Auto-focus next input
        if (index < length - 1) {
          focusIndex(index + 1);
        }
        return;
      }

      // Multiple digits -> distribute across inputs starting at index
      applyAtIndex(index, digits);
    },
    [otpValues, length, disabled, applyOtpValues, focusIndex, applyAtIndex],
  );

  const handleOtpKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent) => {
      if (disabled) return;

      if (e.key === 'Backspace') {
        if (otpValues[index]) {
          const next = [...otpValues];
          next[index] = '';
          applyOtpValues(next);
          return;
        }
        if (!otpValues[index] && index > 0) {
          focusIndex(index - 1);
        }
      }

      if (e.key === 'Enter') {
        const combinedOtp = otpValues.join('');
        if (combinedOtp.length === length && onComplete) {
          onComplete();
        }
      }
    },
    [otpValues, length, onComplete, disabled, applyOtpValues, focusIndex],
  );

  const handlePaste = useCallback(
    (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
      if (disabled) return;
      const text = e.clipboardData.getData('text');
      const digits = onlyDigits(text);
      if (!digits) return;

      e.preventDefault();
      applyAtIndex(index, digits);
    },
    [applyAtIndex, disabled],
  );

  const handleInputBlur = useCallback(() => {
    onBlur?.();
  }, [onBlur]);

  return (
    <JustificationContainer
      direction="column"
      width="100%"
      gap="xs"
      dataTestId={dataTestId}
      className={className}
    >
      {placeholder && (
        <Body size="s" margin={[null]}>
          {placeholder}
        </Body>
      )}
      <JustificationContainer
        direction="row"
        width="100%"
        gap="xs"
        justification="center"
      >
        {otpValues.map((inputValue, index) => (
          <OTPInputElement
            key={index}
            ref={el => {
              inputRefs.current[index] = el;
            }}
            id={`otp-${index}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            // Let the FIRST input accept the full code, so iOS/Android autofill can dump "123456" into it.
            maxLength={index === 0 ? length : 1}
            onChange={e => handleOtpChange(index, e.target.value)}
            onPaste={e => handlePaste(index, e)}
            onKeyDown={e => handleOtpKeyDown(index, e)}
            onBlur={handleInputBlur}
            value={inputValue}
            $disabled={disabled}
            $filled={!!inputValue}
            aria-label={`OTP digit ${index + 1}`}
          />
        ))}
      </JustificationContainer>
      {errorMessage && (
        <ErrorMessage size="xs" margin={[null]}>
          {errorMessage}
        </ErrorMessage>
      )}
    </JustificationContainer>
  );
};

const OTPInputElement = styled.input<{ $disabled: boolean; $filled: boolean }>(
  ({ theme, $disabled, $filled }) => css`
    width: ${theme.space('xxl')};
    height: ${theme.space('xxl')};
    flex-shrink: 0;
    border-radius: ${theme.getTokens().border.radius.base};
    border: ${theme.getTokens().border.width.base} solid
      ${theme.color('info', 'light')};
    text-align: center;
    font-size: ${theme.fs('m')};
    font-weight: ${theme.fw('semiBold')};
    color: ${theme.color('primary', 'dark')};
    outline: none;
    box-sizing: border-box;
    background-color: #fff;

    &:focus {
      border-color: ${theme.color('info', 'base')};
      box-shadow: ${theme.boxShadow('around')};
    }

    ${$disabled &&
    css`
      color: ${theme.color('tertiary', 'dark')};
      background-color: ${theme.color('tertiary', 'light')};
      border-color: ${theme.color('tertiary', 'base')};
      pointer-events: none;
      opacity: 0.6;
    `}

    ${!$disabled &&
    $filled &&
    css`
      border-color: ${theme.color('info', 'base')};
    `}
  `,
);

const ErrorMessage = styled(Body)(
  ({ theme }) => css`
    color: ${theme.color('danger', 'base')};
  `,
);

export default OTPInput;
